/**
 * Aircraft configuration: the schema, its validator, and the loader that turns JSON into the
 * runtime objects the flight model consumes.
 *
 * Non-negotiable #8: aircraft are data. The FDM contains no aircraft-specific branches — a new
 * aeroplane is a new JSON file and a glTF model, nothing else. Everything that distinguishes a
 * trainer from a fighter lives here: mass, inertia, panel layout, aerofoil tables, propulsion,
 * gear geometry, control throws and limits.
 *
 * The validator is hand-written rather than pulled from a schema library because the useful
 * error message is not "expected number at .panels[3].areaM2" but "panel 'wing_outer_left'
 * has an area of 0, which would make it contribute nothing". Configs are authored by hand and
 * are the single most likely place for a silent physical mistake, so the validator checks
 * physical plausibility as well as types.
 *
 * The full schema is documented with a worked example in `src/aircraft/README.md`.
 */

import { AeroTable, type AirfoilSpec, type SampledAeroData } from '../sim/aero/tables.js';
import { AeroPanel, type PanelConfig, type PanelKind } from '../sim/aero/panel.js';
import { LandingGear, type BrakeChannel, type GearConfig, type WheelConfig } from '../sim/gear.js';
import { Propulsion, type PistonEngineConfig, type PropellerConfig } from '../sim/propulsion.js';
import { Turbofan, type JetEngineConfig } from '../sim/jet.js';
import type { Powerplant } from '../sim/powerplant.js';
import type { MassProperties } from '../sim/rigidBody.js';
import type { FlyByWireConfig } from '../sim/fbw.js';
import { validateWeapons, type WeaponsConfig } from './weapons/config.js';
// Type-only, and deliberately so: `audio/soundSet.ts` imports `LoadedAircraft` back from here,
// and a type-only pair of imports is erased at compile time rather than becoming a module cycle.
import type { AircraftSoundConfigJson } from '../audio/soundSet.js';

// ---------------------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------------------

/** Published figures the validation gates assert against. Documentation, not physics. */
export interface ReferencePerformance {
  /** Clean stall, indicated [kt]. */
  stallCleanKias?: number;
  /** Full-flap stall, indicated [kt]. */
  stallFullFlapKias?: number;
  /** Cruise true airspeed at `cruiseAltitudeFt` and `cruisePowerFraction` [kt]. */
  cruiseKtas?: number;
  cruiseAltitudeFt?: number;
  cruisePowerFraction?: number;
  /** Sea-level rate of climb at maximum take-off weight [ft/min]. */
  climbRateFpm?: number;
  /** Take-off ground roll, ISA, sea level, MTOW [ft]. */
  takeoffGroundRollFt?: number;
  /** Best rate-of-climb speed [kt indicated]. */
  bestClimbKias?: number;
  /** Rotation speed [kt indicated]. */
  rotateKias?: number;
}

export interface MassConfig {
  emptyKg: number;
  maxTakeoffKg: number;
  /** Roll inertia [kg·m²]. */
  ixx: number;
  /** Pitch inertia [kg·m²]. */
  iyy: number;
  /** Yaw inertia [kg·m²]. */
  izz: number;
  /** Roll–yaw product of inertia [kg·m²]. */
  ixz: number;
}

export interface GeometryConfig {
  /** Reference wing area [m²]. */
  wingAreaM2: number;
  /** Wing span [m]. */
  wingSpanM: number;
  /** Mean aerodynamic chord [m]. */
  meanChordM: number;
}

/** Aerofoil parameters as they appear in JSON — the generator's input. */
export type AirfoilConfigJson = AirfoilSpec;

export interface PanelConfigJson {
  name: string;
  positionM: [number, number, number];
  areaM2: number;
  chordM: number;
  spanM: number;
  kind: PanelKind;
  incidenceDeg?: number;
  dihedralDeg?: number;
  side?: number;
  airfoil?: AirfoilConfigJson;
  /** Explicit −180…180° data, used instead of a generated table. */
  table?: SampledAeroData;
  /** Aerofoil with the flaps fully deployed; blended in by flap fraction. */
  flappedAirfoil?: AirfoilConfigJson;
  flapDragDelta?: number;
  flapMomentDelta?: number;
  controls?: { elevator?: number; aileron?: number; rudder?: number };
  slipstreamFraction?: number;
  groundEffect?: boolean;
  heightAboveGearM?: number;
  downwashFactor?: number;
  contributesDownwash?: boolean;
}

/** Rudder trim authority as a fraction of rudder range, when a config does not state its own. */
export const DEFAULT_RUDDER_TRIM_FRACTION = 1 / 3;

/**
 * Aileron trim authority as a fraction of aileron range, by default.
 *
 * Smaller than the rudder's share because a lateral trim tab exists to cancel a *rigging* offset
 * — a slightly heavy wing, unequal fuel, one pilot in the left seat — and those are small standing
 * forces. A quarter of aileron travel is generous for all of them and nowhere near enough to fly
 * the aeroplane on trim alone, which is the correct authority for a trim control.
 */
export const DEFAULT_AILERON_TRIM_FRACTION = 1 / 4;

/** Rudder trim authority for this aircraft [deg], applying the default where none is given. */
export function rudderTrimRangeDeg(controls: ControlsConfig): number {
  return controls.rudderTrimRangeDeg ?? controls.rudderRangeDeg * DEFAULT_RUDDER_TRIM_FRACTION;
}

/** Aileron trim authority for this aircraft [deg], applying the default where none is given. */
export function aileronTrimRangeDeg(controls: ControlsConfig): number {
  return controls.aileronTrimRangeDeg ?? controls.aileronRangeDeg * DEFAULT_AILERON_TRIM_FRACTION;
}

export interface ControlsConfig {
  /** Maximum elevator deflection [deg]; trailing edge down is positive. */
  elevatorRangeDeg: number;
  aileronRangeDeg: number;
  rudderRangeDeg: number;
  /**
   * Elevator trim authority [deg]. Trim shifts the *zero point* of the elevator command, so
   * it genuinely relieves stick force rather than adding a parallel input.
   */
  trimRangeDeg: number;
  /**
   * Rudder trim authority [deg]. Same idea as {@link trimRangeDeg}: it shifts the rudder's zero
   * point, so at the trimmed power setting the pilot's feet come off the pedals. Small, because
   * rudder trim on a trainer exists to cancel a standing offset, not to fly the aeroplane with.
   * Optional: a config that omits it gets {@link DEFAULT_RUDDER_TRIM_FRACTION} of the rudder's
   * own range, which is enough to cancel the standing offset on any single-engine trainer.
   */
  rudderTrimRangeDeg?: number;
  /**
   * Aileron trim authority [deg]. The third trim axis, and the same mechanism as the other two:
   * it shifts the aileron's zero point, so a wing that is standing heavy can be trimmed off
   * rather than held off. Optional; see {@link DEFAULT_AILERON_TRIM_FRACTION}.
   */
  aileronTrimRangeDeg?: number;
  /** Flap detents [deg]; the first must be 0. */
  flapDetentsDeg: number[];
  /** Time for the flaps to travel their full range [s]. */
  flapTravelTimeS: number;
  /** Nosewheel steering authority at full pedal [deg]. */
  noseSteerDeg?: number;
}

export interface LimitsConfig {
  /** Never-exceed speed [kt indicated]. */
  vneKias: number;
  /** Maximum flap-extended speed [kt indicated]. */
  vfeKias: number;
  /** Maximum structural cruising speed [kt indicated]. */
  vnoKias?: number;
  gLimitPositive: number;
  gLimitNegative: number;
}

/**
 * Airspeed-indicator position error.
 *
 * Real aircraft publish separate KIAS and KCAS columns because the static source sits in a
 * pressure field that changes with angle of attack — the error is a knot or two in cruise and
 * five or six near the stall, and larger again with flaps down disturbing the flow. The
 * C172S POH stall speeds quoted in the validation gates are *indicated*, so a model that
 * reports calibrated airspeed cannot match them without this. Modelling it explicitly is both
 * more honest and more realistic than bending the aerodynamics to compensate.
 *
 * `IAS = CAS − (perCl + perClWithFlap·flap) · CL`
 */
export interface AirspeedCalibration {
  /** Position error per unit lift coefficient [m/s]. */
  perCl: number;
  /** Additional error per unit CL at full flap [m/s]. */
  perClWithFlap: number;
}

/**
 * A display smoke system — the oil pumped into the exhaust that draws the aeroplane's path.
 *
 * Purely visual, and deliberately so: real display smoke is a few kilograms of oil and its
 * aerodynamic effect is nil, so nothing in `sim/` knows this exists. It lives in the aircraft
 * config rather than in the renderer because *which aeroplane has it* is a property of the
 * aeroplane, and because the emitter has to be positioned in body axes like everything else.
 */
export interface SmokeConfig {
  /** Does this aircraft carry a smoke system at all? */
  available: boolean;
  /** Linear RGB, 0…1. White for display smoke; the tint is per-aircraft so a team can differ. */
  colour: [number, number, number];
  /** Where the oil enters the exhaust, relative to the CG, body axes [m]. */
  emitterPositionM: [number, number, number];
  /** Emission rate [puffs/s]. */
  puffsPerSecond: number;
  /** How long a puff survives before it has dispersed [s]. */
  lifetimeS: number;
}

/**
 * The engine block, tagged by what kind of engine it is.
 *
 * `kind` is optional on a piston because every config written before M6 omitted it and those
 * files are correct as they stand — a missing tag means the thing this simulator started with.
 * The tag is what selects a {@link Powerplant} implementation, and it is the *only* place in the
 * project where the type of engine is branched on: `sim/fdm.ts` never asks (non-negotiable #8).
 */
export type EngineConfigJson =
  | ({ kind?: 'piston' } & PistonEngineConfig)
  | ({ kind: 'turbofan' } & JetEngineConfig);

/** Narrowing helper, so the branch reads the same everywhere it is needed. */
export function isTurbofan(engine: EngineConfigJson): engine is { kind: 'turbofan' } & JetEngineConfig {
  return engine.kind === 'turbofan';
}

/**
 * The handful of numbers about an engine that the *presentation* layers need.
 *
 * The instruments want to draw an arc, the audio wants a reference to scale loudness against, and
 * neither has any business knowing whether it is looking at a crankshaft or a compressor spool.
 * Without this every consumer grows its own `isTurbofan` branch, which is how "the FDM has no
 * aircraft-specific branches" quietly becomes true of the FDM and false of everything else.
 *
 * The speed figures are in whatever unit that engine's `rpm` is quoted in — see the note on
 * {@link Powerplant} — so a gauge built from them is correctly labelled either way.
 */
export interface EngineReference {
  /** Full-scale deflection for the engine-speed gauge. */
  speedGaugeMaximum: number;
  /** Where the redline sits on it. */
  speedRedline: number;
  /** The normal operating band, for the green arc. */
  speedGreenLow: number;
  speedGreenHigh: number;
  /**
   * What "full power" means for this engine, in newtons of thrust or watts of shaft power.
   *
   * Only ever used as a *ratio* — the audio scales engine loudness by how much of it is being
   * produced — so the units differing between engine types is not a problem, and forcing a
   * turbofan's thrust into watts would be inventing a number rather than avoiding one.
   */
  fullPowerReference: number;
}

export function engineReference(engine: EngineConfigJson): EngineReference {
  if (isTurbofan(engine)) {
    // N2, as a percentage. A turbine gauge is 0–110 % with the band up at the top, because a jet
    // spends its life near military power and idles at 60-odd per cent.
    return {
      speedGaugeMaximum: 110,
      speedRedline: engine.militaryN2Percent,
      speedGreenLow: engine.idleN2Percent,
      speedGreenHigh: engine.militaryN2Percent,
      fullPowerReference: engine.militaryThrustN,
    };
  }
  return {
    speedGaugeMaximum: Math.ceil((engine.maxRpm * 1.1) / 100) * 100,
    speedRedline: engine.maxRpm,
    speedGreenLow: Math.round(engine.ratedRpm * 0.78),
    speedGreenHigh: Math.round(engine.ratedRpm * 0.93),
    fullPowerReference: engine.ratedPowerW,
  };
}

/** Idle speed for this engine, in its own units — the value it spawns and settles at. */
export function engineIdleSpeed(engine: EngineConfigJson): number {
  return isTurbofan(engine) ? engine.idleN2Percent : engine.ratedRpm / 4;
}

/**
 * Airbrake geometry.
 *
 * Modelled as drag area rather than as a panel, deliberately. A speedbrake is a flat plate normal
 * to the flow and its lift is nil by construction, so the panel machinery — a full −180…180° table,
 * a chord axis, a downwash factor — would be five parameters of ceremony around one number. What
 * it *does* have that matters is a moment arm: a fighter's boards open above the tailpipe and the
 * pitch change as they deploy is something a pilot trims out, so the position is real.
 */
export interface SpeedbrakeConfig {
  /** Drag area at full deployment, `C_D·S` [m²]. */
  dragAreaM2: number;
  /** Time to travel from closed to fully open [s]. */
  travelTimeS: number;
  /** Centre of pressure of the boards relative to the CG, body axes [m]. */
  positionM: [number, number, number];
}

/**
 * Compressibility drag: the transonic rise, as a whole-aircraft drag coefficient increment.
 *
 * The panel tables are incompressible, which is correct and sufficient for everything that flies
 * below about M0.6 — and completely wrong for a fighter. Without a wave-drag term a supersonic
 * aeroplane has nothing opposing it above the speed of sound at all: it does not merely reach
 * M1.6, it accelerates through M3 and keeps going, and the gate that was supposed to *test*
 * supersonic performance passes for the one reason that makes the test worthless.
 *
 * Modelled as an increment on `CD₀` referenced to the wing area, applied by the FDM as a body
 * force along the relative wind, rather than folded into the panel tables. Three reasons: wave
 * drag is a property of the *whole configuration* (the area distribution, the fuselage, the
 * intake) and not of any one strip of wing; splitting it across panels by area would double-count
 * the fuselage, which is where most of it comes from; and this way an aircraft that omits the
 * block is exactly as fast as it was before the feature existed.
 *
 * The shape of the curve is the physics: nothing below the critical Mach, a steep rise through
 * the drag divergence around M0.95, a peak just past M1, and a slow decline after it as the
 * shock system stabilises. An aeroplane with enough thrust to push through the peak finds it
 * *easier* on the other side, which is why supersonic acceleration feels the way it does.
 */
export interface CompressibilityConfig {
  /** Flight Mach numbers. Must start at 0. */
  machNumbers: number[];
  /** `ΔCD₀` at those Mach numbers, referenced to the wing area. Must start at 0. */
  waveDragCoefficient: number[];
  /**
   * **Mach tuck** — `ΔCm` at those Mach numbers, referenced to wing area and mean chord.
   *
   * Negative is nose-down, which is the direction this always goes. As the flow over the wing
   * goes supersonic the shock forms aft of the crest and the centre of pressure moves back from
   * roughly a quarter chord toward a half, and a lift force applied further aft is a nose-down
   * moment. On a swept wing the outboard sections shock first, and they are the aft ones, so it
   * is sharper still. It was violent enough on the early jets to be unrecoverable, and it is why
   * a Mach trimmer exists at all.
   *
   * Referenced to `wingAreaM2 · meanChordM`, so it is the ordinary non-dimensional
   * pitching-moment coefficient and can be read straight off a wind-tunnel plot.
   *
   * Optional. Omit it and the aircraft has no tuck — right for anything that cannot approach
   * its critical Mach, which is both propeller aeroplanes in the fleet.
   */
  pitchingMomentCoefficient?: number[];
  /**
   * **Transonic control effectiveness** — a multiplier on control-surface authority, 0…1.
   *
   * A trailing-edge surface works by changing the circulation over the whole section ahead of
   * it. Once the section is shocked it cannot: the shock isolates the surface from the flow
   * upstream of it, and the deflection only moves the air behind the shock. Roll authority is
   * the worst hit — an aileron is the thinnest, most outboard, most shocked thing on the
   * aeroplane, and aileron reversal is the extreme case of it.
   *
   * The recovery supersonically is real but partial for a trailing-edge surface, and complete
   * for an all-moving one — which is precisely why every supersonic aeroplane has a slab
   * tailplane instead of an elevator. The fleet's fighter has one, so its curve recovers.
   *
   * Optional; absent means full authority everywhere, which is what a propeller aeroplane has.
   */
  controlEffectiveness?: number[];
}

/** Retractable undercarriage: how long it takes, and what it costs while it is out. */
export interface RetractableGearConfig {
  /** Time from locked down to locked up [s]. */
  travelTimeS: number;
  /** Drag area with the gear fully extended, `C_D·S` [m²]. */
  dragAreaM2: number;
  /** Maximum speed at which the gear may be extended [kt indicated]. */
  maxExtendKias: number;
}

export type { FlyByWireConfig, WeaponsConfig };

export interface AircraftConfigJson {
  id: string;
  name: string;
  /** Free text for the aircraft picker. */
  description?: string;
  /** Broad class, used for UI grouping and for choosing a control law. */
  category: 'trainer' | 'aerobatic' | 'fighter';
  reference?: ReferencePerformance;
  mass: MassConfig;
  geometry: GeometryConfig;
  panels: PanelConfigJson[];
  engine: EngineConfigJson;
  /** Absent on a jet, which has no propeller to describe. */
  propeller?: PropellerConfig;
  gear: {
    wheels: Array<Omit<WheelConfig, 'positionM'> & { positionM: [number, number, number] }>;
    frictionReferenceSpeedMps?: number;
    /** Present when the gear retracts. Absent means it is welded down, like the trainer's. */
    retractable?: RetractableGearConfig;
  };
  /** Airbrake, if this aircraft has one. */
  speedbrake?: SpeedbrakeConfig;
  /** Fly-by-wire control laws. Absent means the stick moves the surfaces directly. */
  flyByWire?: FlyByWireConfig;
  /** Transonic drag rise. Absent means the aircraft never goes fast enough to need one. */
  compressibility?: CompressibilityConfig;
  controls: ControlsConfig;
  limits: LimitsConfig;
  airspeedCalibration?: AirspeedCalibration;
  /** Propeller hub position relative to the CG, body axes [m]. */
  propellerPositionM?: [number, number, number];
  /** Eye point for the cockpit camera, body axes [m]. */
  eyePointM?: [number, number, number];
  /** Display smoke, if this aircraft carries it. Absent means it does not. */
  smoke?: SmokeConfig;
  /**
   * What this aircraft is armed with. **Absent means unarmed**, and that is the entire mechanism
   * by which the trainer and the aerobat stay unarmed — not a category check, an absent object.
   * See `weapons/config.ts`.
   */
  weapons?: WeaponsConfig;
  /**
   * The audio sound set. Everything in it is optional and everything omitted is derived from
   * the physics config — see `src/audio/soundSet.ts` for what is derived from what, and
   * `src/aircraft/README.md` for the schema.
   */
  sound?: AircraftSoundConfigJson;
}

// ---------------------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------------------

export class AircraftConfigError extends Error {
  readonly issues: readonly string[];

  constructor(id: string, issues: readonly string[]) {
    super(`Aircraft config "${id}" is invalid:\n  - ${issues.join('\n  - ')}`);
    this.name = 'AircraftConfigError';
    this.issues = issues;
  }
}

/** Collects problems so the author sees all of them at once, not one per run. */
class IssueList {
  readonly issues: string[] = [];

  require(condition: boolean, message: string): void {
    if (!condition) this.issues.push(message);
  }

  positive(value: unknown, path: string): value is number {
    if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
      this.issues.push(`${path} must be a positive finite number (got ${String(value)})`);
      return false;
    }
    return true;
  }

  finite(value: unknown, path: string): value is number {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      this.issues.push(`${path} must be a finite number (got ${String(value)})`);
      return false;
    }
    return true;
  }

  vector3(value: unknown, path: string): boolean {
    if (!Array.isArray(value) || value.length !== 3 || value.some((v) => typeof v !== 'number' || !Number.isFinite(v))) {
      this.issues.push(`${path} must be an array of three finite numbers`);
      return false;
    }
    return true;
  }
}

/**
 * Validate a config, checking physical plausibility as well as structure.
 * Throws {@link AircraftConfigError} listing every problem found.
 */
export function validateAircraftConfig(config: AircraftConfigJson): void {
  const issues = new IssueList();

  issues.require(typeof config.id === 'string' && config.id.length > 0, 'id must be a non-empty string');
  issues.require(typeof config.name === 'string' && config.name.length > 0, 'name must be a non-empty string');

  // --- mass ---------------------------------------------------------------------------
  const mass = config.mass;
  if (mass === undefined) {
    issues.issues.push('mass is required');
  } else {
    issues.positive(mass.emptyKg, 'mass.emptyKg');
    issues.positive(mass.maxTakeoffKg, 'mass.maxTakeoffKg');
    issues.positive(mass.ixx, 'mass.ixx');
    issues.positive(mass.iyy, 'mass.iyy');
    issues.positive(mass.izz, 'mass.izz');
    issues.finite(mass.ixz, 'mass.ixz');
    issues.require(
      mass.maxTakeoffKg >= mass.emptyKg,
      `mass.maxTakeoffKg (${mass.maxTakeoffKg}) must be at least mass.emptyKg (${mass.emptyKg})`,
    );
    if (Number.isFinite(mass.ixx) && Number.isFinite(mass.izz) && Number.isFinite(mass.ixz)) {
      issues.require(
        mass.ixx * mass.izz > mass.ixz * mass.ixz,
        `mass.ixz (${mass.ixz}) is too large for ixx·izz — the inertia tensor would not be positive definite`,
      );
    }
    // A pitch inertia larger than the yaw inertia means a longer wing than fuselage; possible
    // for a flying wing, wrong for everything this project ships.
    issues.require(
      mass.izz >= mass.iyy * 0.5,
      `mass.izz (${mass.izz}) is implausibly small next to iyy (${mass.iyy})`,
    );
  }

  // --- geometry -----------------------------------------------------------------------
  const geometry = config.geometry;
  if (geometry === undefined) {
    issues.issues.push('geometry is required');
  } else {
    issues.positive(geometry.wingAreaM2, 'geometry.wingAreaM2');
    issues.positive(geometry.wingSpanM, 'geometry.wingSpanM');
    issues.positive(geometry.meanChordM, 'geometry.meanChordM');
    if (geometry.wingAreaM2 > 0 && geometry.wingSpanM > 0) {
      const aspectRatio = (geometry.wingSpanM * geometry.wingSpanM) / geometry.wingAreaM2;
      issues.require(
        aspectRatio > 1 && aspectRatio < 40,
        `geometry implies an aspect ratio of ${aspectRatio.toFixed(1)}, which is outside anything flyable`,
      );
    }
  }

  // --- panels -------------------------------------------------------------------------
  if (!Array.isArray(config.panels) || config.panels.length === 0) {
    issues.issues.push('panels must be a non-empty array');
  } else {
    const names = new Set<string>();
    let totalHorizontalArea = 0;
    let hasVertical = false;

    for (let i = 0; i < config.panels.length; i++) {
      const panel = config.panels[i];
      const label = `panels[${i}]${panel?.name ? ` ("${panel.name}")` : ''}`;

      if (typeof panel.name !== 'string' || panel.name.length === 0) {
        issues.issues.push(`${label} needs a name`);
      } else if (names.has(panel.name)) {
        issues.issues.push(`${label} duplicates the name "${panel.name}"`);
      } else {
        names.add(panel.name);
      }

      issues.vector3(panel.positionM, `${label}.positionM`);
      issues.positive(panel.areaM2, `${label}.areaM2`);
      issues.positive(panel.chordM, `${label}.chordM`);
      issues.positive(panel.spanM, `${label}.spanM`);
      issues.require(
        panel.kind === 'horizontal' || panel.kind === 'vertical',
        `${label}.kind must be "horizontal" or "vertical"`,
      );
      issues.require(
        panel.airfoil !== undefined || panel.table !== undefined,
        `${label} needs either an airfoil or an explicit table`,
      );
      if (panel.airfoil !== undefined) validateAirfoil(panel.airfoil, `${label}.airfoil`, issues);
      if (panel.flappedAirfoil !== undefined) {
        validateAirfoil(panel.flappedAirfoil, `${label}.flappedAirfoil`, issues);
      }
      if (panel.slipstreamFraction !== undefined) {
        issues.require(
          panel.slipstreamFraction >= 0 && panel.slipstreamFraction <= 1,
          `${label}.slipstreamFraction must be between 0 and 1`,
        );
      }

      if (panel.kind === 'horizontal') totalHorizontalArea += panel.areaM2 ?? 0;
      if (panel.kind === 'vertical') hasVertical = true;
    }

    issues.require(hasVertical, 'no vertical panel: the aircraft would have no directional stability');

    if (geometry?.wingAreaM2 > 0) {
      // The horizontal panels include the wing, tailplane and fuselage, so their total should
      // be in the same ballpark as the reference wing area — a factor-of-two error here means
      // somebody mixed up feet and metres.
      const ratio = totalHorizontalArea / geometry.wingAreaM2;
      issues.require(
        ratio > 0.8 && ratio < 2.5,
        `horizontal panel area totals ${totalHorizontalArea.toFixed(1)} m² against a reference wing area of ` +
          `${geometry.wingAreaM2} m² (ratio ${ratio.toFixed(2)}) — check the units`,
      );
    }
  }

  // --- propulsion ---------------------------------------------------------------------
  /*
   * The one place in the project that branches on what kind of engine an aeroplane has.
   *
   * `sim/fdm.ts` never asks (non-negotiable #8) — it talks to a `Powerplant`. The decision is
   * made here, once, from data, and the two branches validate genuinely different physics: a
   * piston is a torque balance with a propeller on the end of it, a turbofan is a commanded spool
   * with a thrust table.
   */
  const engine = config.engine;
  if (engine === undefined) {
    issues.issues.push('engine is required');
  } else if (isTurbofan(engine)) {
    validateTurbofan(engine, config.propeller, issues);
  } else {
    issues.positive(engine.ratedPowerW, 'engine.ratedPowerW');
    issues.positive(engine.ratedRpm, 'engine.ratedRpm');
    issues.positive(engine.maxRpm, 'engine.maxRpm');
    issues.positive(engine.inertiaKgM2, 'engine.inertiaKgM2');
    issues.require(
      engine.rotationSense === 1 || engine.rotationSense === -1,
      'engine.rotationSense must be +1 (clockwise from the cockpit) or −1',
    );
    issues.require(
      engine.idlePowerFraction >= 0 && engine.idlePowerFraction < 1,
      'engine.idlePowerFraction must be in [0, 1)',
    );
    validateCurve(
      engine.powerCurveRpmFraction,
      engine.powerCurvePowerFraction,
      'engine.powerCurve',
      issues,
    );

    const propeller = config.propeller;
    if (propeller === undefined) {
      issues.issues.push('propeller is required for a piston engine');
    } else {
      issues.positive(propeller.diameterM, 'propeller.diameterM');
      validateCurve(propeller.advanceRatio, propeller.thrustCoefficient, 'propeller.thrustCoefficient', issues);
      validateCurve(propeller.advanceRatio, propeller.powerCoefficient, 'propeller.powerCoefficient', issues);
      if (Array.isArray(propeller.thrustCoefficient) && propeller.thrustCoefficient.length > 0) {
        issues.require(
          propeller.thrustCoefficient[0] > 0,
          'propeller.thrustCoefficient must be positive at zero advance ratio, or the aircraft cannot start rolling',
        );
      }
      if (Array.isArray(propeller.powerCoefficient)) {
        issues.require(
          propeller.powerCoefficient.every((value) => value > 0),
          'propeller.powerCoefficient must be positive everywhere, or the propeller would drive the engine',
        );
      }
    }
  }

  // --- gear ---------------------------------------------------------------------------
  if (config.gear === undefined || !Array.isArray(config.gear.wheels) || config.gear.wheels.length === 0) {
    issues.issues.push('gear.wheels must be a non-empty array');
  } else {
    let hasBelowCg = false;
    for (let i = 0; i < config.gear.wheels.length; i++) {
      const wheel = config.gear.wheels[i];
      const label = `gear.wheels[${i}]${wheel?.name ? ` ("${wheel.name}")` : ''}`;
      issues.vector3(wheel.positionM, `${label}.positionM`);
      issues.positive(wheel.springRateNPerM, `${label}.springRateNPerM`);
      issues.positive(wheel.dampingNsPerM, `${label}.dampingNsPerM`);
      issues.positive(wheel.maxTravelM, `${label}.maxTravelM`);
      issues.require(
        wheel.lateralFriction > wheel.rollingResistance,
        `${label} has lateral friction (${wheel.lateralFriction}) no greater than rolling resistance ` +
          `(${wheel.rollingResistance}) — it would slide sideways like a shopping trolley`,
      );
      if (Array.isArray(wheel.positionM) && wheel.positionM[2] > 0) hasBelowCg = true;
    }
    issues.require(hasBelowCg, 'no wheel sits below the centre of gravity (positionM z must be positive, down)');
  }

  // --- controls and limits --------------------------------------------------------------
  const controls = config.controls;
  if (controls === undefined) {
    issues.issues.push('controls is required');
  } else {
    issues.positive(controls.elevatorRangeDeg, 'controls.elevatorRangeDeg');
    issues.positive(controls.aileronRangeDeg, 'controls.aileronRangeDeg');
    issues.positive(controls.rudderRangeDeg, 'controls.rudderRangeDeg');
    issues.finite(controls.trimRangeDeg, 'controls.trimRangeDeg');
    if (controls.rudderTrimRangeDeg !== undefined) {
      issues.finite(controls.rudderTrimRangeDeg, 'controls.rudderTrimRangeDeg');
    }
    if (controls.aileronTrimRangeDeg !== undefined) {
      issues.finite(controls.aileronTrimRangeDeg, 'controls.aileronTrimRangeDeg');
    }
    if (!Array.isArray(controls.flapDetentsDeg) || controls.flapDetentsDeg.length === 0) {
      issues.issues.push('controls.flapDetentsDeg must be a non-empty array');
    } else {
      issues.require(controls.flapDetentsDeg[0] === 0, 'controls.flapDetentsDeg must start at 0 (flaps up)');
      for (let i = 1; i < controls.flapDetentsDeg.length; i++) {
        issues.require(
          controls.flapDetentsDeg[i] > controls.flapDetentsDeg[i - 1],
          `controls.flapDetentsDeg must increase (index ${i})`,
        );
      }
    }
  }

  const limits = config.limits;
  if (limits === undefined) {
    issues.issues.push('limits is required');
  } else {
    issues.positive(limits.vneKias, 'limits.vneKias');
    issues.positive(limits.vfeKias, 'limits.vfeKias');
    issues.require(
      limits.vfeKias < limits.vneKias,
      `limits.vfeKias (${limits.vfeKias}) must be below vneKias (${limits.vneKias})`,
    );
    issues.require(limits.gLimitPositive > 1, 'limits.gLimitPositive must exceed 1');
    issues.require(limits.gLimitNegative < 0, 'limits.gLimitNegative must be negative');
  }

  // --- compressibility --------------------------------------------------------------------
  const compressibility = config.compressibility;
  if (compressibility !== undefined) {
    validateCurve(
      compressibility.machNumbers,
      compressibility.waveDragCoefficient,
      'compressibility.waveDragCoefficient',
      issues,
    );
    if (Array.isArray(compressibility.machNumbers) && compressibility.machNumbers.length > 0) {
      issues.require(
        compressibility.machNumbers[0] === 0,
        'compressibility.machNumbers must start at 0',
      );
    }
    if (Array.isArray(compressibility.waveDragCoefficient)) {
      issues.require(
        compressibility.waveDragCoefficient[0] === 0,
        'compressibility.waveDragCoefficient must be 0 at Mach 0 — it is an increment, not a total',
      );
      issues.require(
        compressibility.waveDragCoefficient.every((value) => value >= 0),
        'compressibility.waveDragCoefficient cannot be negative; wave drag does not push',
      );
    }

    if (compressibility.pitchingMomentCoefficient !== undefined) {
      validateCurve(
        compressibility.machNumbers,
        compressibility.pitchingMomentCoefficient,
        'compressibility.pitchingMomentCoefficient',
        issues,
      );
      if (Array.isArray(compressibility.pitchingMomentCoefficient)) {
        issues.require(
          compressibility.pitchingMomentCoefficient[0] === 0,
          'compressibility.pitchingMomentCoefficient must be 0 at Mach 0 — it is an increment on ' +
            'the panel model, not a total',
        );
        // Tuck is nose-down by definition. A positive entry here is a sign error, and a sign
        // error in a pitching moment is an aeroplane that pitches *up* into the drag rise.
        issues.require(
          compressibility.pitchingMomentCoefficient.every((value) => value <= 0),
          'compressibility.pitchingMomentCoefficient cannot be positive; Mach tuck is nose-down',
        );
      }
    }

    if (compressibility.controlEffectiveness !== undefined) {
      validateCurve(
        compressibility.machNumbers,
        compressibility.controlEffectiveness,
        'compressibility.controlEffectiveness',
        issues,
      );
      if (Array.isArray(compressibility.controlEffectiveness)) {
        issues.require(
          compressibility.controlEffectiveness[0] === 1,
          'compressibility.controlEffectiveness must be 1 at Mach 0 — it is a multiplier on the ' +
            'surface authority the panels already model, and subsonic flight is the baseline',
        );
        // The floor is not zero: a surface with literally no authority is an aeroplane with no
        // way out of the region, and it is also not what happens — some authority always remains.
        issues.require(
          compressibility.controlEffectiveness.every((value) => value > 0 && value <= 1),
          'compressibility.controlEffectiveness must be in (0, 1]',
        );
      }
    }
  }

  // --- smoke ----------------------------------------------------------------------------
  const smoke = config.smoke;
  if (smoke !== undefined) {
    issues.vector3(smoke.emitterPositionM, 'smoke.emitterPositionM');
    if (!Array.isArray(smoke.colour) || smoke.colour.length !== 3) {
      issues.issues.push('smoke.colour must be three numbers');
    } else {
      issues.require(
        smoke.colour.every((channel) => channel >= 0 && channel <= 1),
        'smoke.colour channels must be in 0…1 (linear RGB, not 0…255)',
      );
    }
    issues.positive(smoke.puffsPerSecond, 'smoke.puffsPerSecond');
    issues.positive(smoke.lifetimeS, 'smoke.lifetimeS');
    // A puff budget: rate × lifetime is the steady-state population, and the pool is sized from
    // it. Ten thousand billboards is not a smoke trail, it is a frame-rate bug with a nice name.
    issues.require(
      smoke.puffsPerSecond * smoke.lifetimeS <= 2_000,
      `smoke would hold ${Math.round(smoke.puffsPerSecond * smoke.lifetimeS)} puffs alive at once ` +
        '(rate × lifetime); keep it under 2000',
    );
  }

  // --- weapons ----------------------------------------------------------------------------
  if (config.weapons !== undefined) validateWeapons(config.weapons, issues);

  if (issues.issues.length > 0) throw new AircraftConfigError(config.id ?? '<unnamed>', issues.issues);
}

function validateAirfoil(airfoil: AirfoilConfigJson, path: string, issues: IssueList): void {
  issues.positive(airfoil.liftSlopePerRad, `${path}.liftSlopePerRad`);
  issues.finite(airfoil.zeroLiftAlphaDeg, `${path}.zeroLiftAlphaDeg`);
  issues.positive(airfoil.stallAlphaDeg, `${path}.stallAlphaDeg`);
  issues.finite(airfoil.negativeStallAlphaDeg, `${path}.negativeStallAlphaDeg`);
  issues.finite(airfoil.clMax, `${path}.clMax`);
  issues.finite(airfoil.clMin, `${path}.clMin`);
  issues.positive(airfoil.cd0, `${path}.cd0`);
  issues.positive(airfoil.inducedDragFactor, `${path}.inducedDragFactor`);
  issues.positive(airfoil.stallSharpnessDeg, `${path}.stallSharpnessDeg`);
  issues.finite(airfoil.cm0, `${path}.cm0`);

  issues.require(airfoil.clMax > 0, `${path}.clMax must be positive`);
  issues.require(airfoil.clMin < airfoil.clMax, `${path}.clMin must be below clMax`);
  issues.require(
    airfoil.negativeStallAlphaDeg < 0,
    `${path}.negativeStallAlphaDeg must be negative`,
  );
  issues.require(
    airfoil.stallAlphaDeg < 40,
    `${path}.stallAlphaDeg of ${airfoil.stallAlphaDeg}° is past where any aerofoil stays attached`,
  );
  issues.require(
    airfoil.clMax < 4,
    `${path}.clMax of ${airfoil.clMax} is beyond even a multi-slotted high-lift system`,
  );
}

function validateCurve(
  xs: readonly number[] | undefined,
  ys: readonly number[] | undefined,
  path: string,
  issues: IssueList,
): void {
  if (!Array.isArray(xs) || !Array.isArray(ys)) {
    issues.issues.push(`${path} needs both an x and a y array`);
    return;
  }
  if (xs.length !== ys.length) {
    issues.issues.push(`${path}: x has ${xs.length} points but y has ${ys.length}`);
    return;
  }
  if (xs.length < 2) {
    issues.issues.push(`${path} needs at least two points`);
    return;
  }
  for (let i = 1; i < xs.length; i++) {
    if (xs[i] <= xs[i - 1]) {
      issues.issues.push(`${path}: x must increase strictly (index ${i}: ${xs[i - 1]} → ${xs[i]})`);
      return;
    }
  }
}

// ---------------------------------------------------------------------------------------
// Loading
// ---------------------------------------------------------------------------------------

/** A validated config plus the runtime objects built from it. */
export interface LoadedAircraft {
  config: AircraftConfigJson;
  massProperties: MassProperties;
  panels: AeroPanel[];
  propulsion: Powerplant;
  gear: LandingGear;
  /** Propeller hub position relative to the CG, body axes [m]. */
  propellerPositionM: readonly [number, number, number];
  airspeedCalibration: AirspeedCalibration;
}

/**
 * Validate a config and build its runtime objects. Throws {@link AircraftConfigError} with
 * every problem listed if the config does not hold up.
 */
export function loadAircraft(config: AircraftConfigJson): LoadedAircraft {
  validateAircraftConfig(config);

  const panels = config.panels.map((panelJson) => new AeroPanel(buildPanelConfig(panelJson)));

  const gearConfig: GearConfig = {
    wheels: config.gear.wheels.map(
      (wheel): WheelConfig => ({
        ...wheel,
        positionM: wheel.positionM,
        brakeChannel: wheel.brakeChannel as BrakeChannel,
      }),
    ),
    ...(config.gear.frictionReferenceSpeedMps === undefined
      ? {}
      : { frictionReferenceSpeedMps: config.gear.frictionReferenceSpeedMps }),
  };

  return {
    config,
    massProperties: {
      massKg: config.mass.maxTakeoffKg,
      ixx: config.mass.ixx,
      iyy: config.mass.iyy,
      izz: config.mass.izz,
      ixz: config.mass.ixz,
    },
    panels,
    propulsion: buildPowerplant(config),
    gear: new LandingGear(gearConfig),
    propellerPositionM: config.propellerPositionM ?? [1.2, 0, 0],
    airspeedCalibration: config.airspeedCalibration ?? { perCl: 0, perClWithFlap: 0 },
  };
}

/**
 * Build the powerplant this config describes.
 *
 * The whole of non-negotiable #8 comes down to this function existing and nothing like it
 * existing anywhere in `sim/`. A jet is not a special case in the flight model; it is a different
 * implementation of {@link Powerplant}, chosen here from data and never asked about again.
 */
function buildPowerplant(config: AircraftConfigJson): Powerplant {
  if (isTurbofan(config.engine)) return new Turbofan(config.engine);
  // The validator has already refused a piston config with no propeller, so this is safe.
  return new Propulsion(config.engine, config.propeller as PropellerConfig);
}

function validateTurbofan(
  engine: JetEngineConfig,
  propeller: PropellerConfig | undefined,
  issues: IssueList,
): void {
  issues.positive(engine.militaryThrustN, 'engine.militaryThrustN');
  issues.positive(engine.spoolTimeS, 'engine.spoolTimeS');
  issues.positive(engine.afterburnerLightTimeS, 'engine.afterburnerLightTimeS');
  issues.positive(engine.startTimeS, 'engine.startTimeS');
  issues.positive(engine.militaryN2Percent, 'engine.militaryN2Percent');
  issues.positive(engine.idleN2Percent, 'engine.idleN2Percent');
  issues.positive(engine.densityLapseExponent, 'engine.densityLapseExponent');
  issues.positive(engine.militaryFuelFlowKgPerS, 'engine.militaryFuelFlowKgPerS');
  issues.require(
    engine.spoolDownFactor >= 1,
    `engine.spoolDownFactor (${engine.spoolDownFactor}) must be at least 1 — a compressor gives ` +
      'its energy back more slowly than it takes it',
  );
  issues.require(
    engine.afterburnerThrustRatio >= 1,
    'engine.afterburnerThrustRatio must be at least 1, or reheat would remove thrust',
  );
  issues.require(
    engine.afterburnerFuelFlowRatio >= engine.afterburnerThrustRatio,
    `reheat that costs less fuel than it gains thrust (${engine.afterburnerFuelFlowRatio}× fuel for ` +
      `${engine.afterburnerThrustRatio}× thrust) would be a better engine than the dry one, and ` +
      'nobody would ever fly it dry',
  );
  issues.require(
    engine.afterburnerGate > 0 && engine.afterburnerGate <= 1,
    'engine.afterburnerGate must be in (0, 1]; 1 means a dry engine',
  );
  issues.require(
    engine.idleThrustFraction >= 0 && engine.idleThrustFraction < 1,
    'engine.idleThrustFraction must be in [0, 1)',
  );
  issues.require(
    engine.idleN2Percent < engine.militaryN2Percent,
    `engine.idleN2Percent (${engine.idleN2Percent}) must be below militaryN2Percent ` +
      `(${engine.militaryN2Percent})`,
  );
  validateCurve(engine.machNumbers, engine.machThrustFactor, 'engine.machThrustFactor', issues);
  if (Array.isArray(engine.machNumbers) && engine.machNumbers.length > 0) {
    issues.require(
      engine.machNumbers[0] === 0,
      'engine.machNumbers must start at 0 — static thrust is the value everything else scales from',
    );
  }
  if (Array.isArray(engine.machThrustFactor) && engine.machThrustFactor.length > 0) {
    issues.require(
      Math.abs(engine.machThrustFactor[0] - 1) < 1e-6,
      'engine.machThrustFactor must be exactly 1 at Mach 0, since militaryThrustN is the static value',
    );
    issues.require(
      engine.machThrustFactor.every((factor) => factor > 0),
      'engine.machThrustFactor must stay positive; a jet does not produce negative thrust',
    );
  }
  issues.require(
    propeller === undefined,
    'a turbofan config must not also carry a propeller block',
  );
}

function buildPanelConfig(json: PanelConfigJson): PanelConfig {
  const table =
    json.table !== undefined ? AeroTable.fromSamples(json.table) : AeroTable.fromAirfoil(json.airfoil as AirfoilSpec);

  const config: PanelConfig = {
    name: json.name,
    positionM: json.positionM,
    areaM2: json.areaM2,
    chordM: json.chordM,
    spanM: json.spanM,
    kind: json.kind,
    table,
  };

  if (json.incidenceDeg !== undefined) config.incidenceDeg = json.incidenceDeg;
  if (json.dihedralDeg !== undefined) config.dihedralDeg = json.dihedralDeg;
  if (json.side !== undefined) config.side = json.side;
  if (json.flappedAirfoil !== undefined) config.flappedTable = AeroTable.fromAirfoil(json.flappedAirfoil);
  if (json.flapDragDelta !== undefined) config.flapDragDelta = json.flapDragDelta;
  if (json.flapMomentDelta !== undefined) config.flapMomentDelta = json.flapMomentDelta;
  if (json.controls !== undefined) config.controls = json.controls;
  if (json.slipstreamFraction !== undefined) config.slipstreamFraction = json.slipstreamFraction;
  if (json.groundEffect !== undefined) config.groundEffect = json.groundEffect;
  if (json.heightAboveGearM !== undefined) config.heightAboveGearM = json.heightAboveGearM;
  if (json.downwashFactor !== undefined) config.downwashFactor = json.downwashFactor;
  if (json.contributesDownwash !== undefined) config.contributesDownwash = json.contributesDownwash;

  return config;
}
